/**
 * Medication not-given / deviation documentation helpers.
 */

export const MEDICATION_DEVIATION_REASONS = [
  { id: 'patient_refused', labelKey: 'deviationPatientRefused' },
  { id: 'patient_away', labelKey: 'deviationPatientAway' },
  { id: 'med_not_available', labelKey: 'deviationMedNotAvailable' },
  { id: 'patient_npo', labelKey: 'deviationPatientNpo' },
  { id: 'physician_held', labelKey: 'deviationPhysicianHeld' },
  { id: 'vitals_outside', labelKey: 'deviationVitalsOutside' },
  { id: 'patient_vomited', labelKey: 'deviationPatientVomited' },
  { id: 'other', labelKey: 'deviationOther' },
];

export function getDeviationReasonLabel(t, reasonId, otherText = '') {
  if (reasonId === 'other') {
    const custom = String(otherText || '').trim();
    return custom || t('deviationOther');
  }
  const entry = MEDICATION_DEVIATION_REASONS.find((item) => item.id === reasonId);
  return entry ? t(entry.labelKey) : String(reasonId || '').trim();
}

export function formatDoseLabel(med) {
  if (!med) return '';
  if (med.dose_amount != null && med.dose_unit) {
    return `${med.dose_amount}${med.dose_unit}`;
  }
  if (med.dose_amount != null) return String(med.dose_amount);
  return String(med.dose || '').trim();
}

/** Build note content for a not-given medication from the prefilled MAR med. */
export function buildNotGivenMedicationNote(existingNote, {
  reasonId,
  reasonText,
  nurseId = null,
  documentedAt = null,
} = {}) {
  const meds = existingNote?.medications_administered?.length
    ? existingNote.medications_administered
    : existingNote?.medications || [];
  const base = meds[0] || {};
  const timestamp = documentedAt || new Date().toISOString();

  const med = {
    ...base,
    drug_name: base.drug_name || base.name || '',
    name: base.name || base.drug_name || '',
    dose_amount: base.dose_amount ?? null,
    dose_unit: base.dose_unit || null,
    dose: base.dose || formatDoseLabel(base),
    route: base.route || '',
    scheduled_time: base.scheduled_time || base.time_given || null,
    time_given: base.time_given || base.scheduled_time || null,
    not_given: true,
    deviation_reason_id: reasonId || null,
    deviation_reason: String(reasonText || '').trim(),
    nurse_id: nurseId,
    documented_at: timestamp,
    confidence: 'high',
  };

  return {
    ...(existingNote || {}),
    note_type: 'medication_admin',
    medications_administered: [med],
    medications: [med],
    unverified_medications: [],
  };
}

export function resolveDeviationReasonText(t, med) {
  if (!med) return '';
  if (med.deviation_reason_id === 'other') {
    return String(med.deviation_reason || t('deviationOther')).trim();
  }
  if (med.deviation_reason_id) {
    return getDeviationReasonLabel(t, med.deviation_reason_id, med.deviation_reason);
  }
  return String(med.deviation_reason || '').trim();
}

export function splitMedicationsByGiven(note) {
  const meds = note?.medications_administered?.length
    ? note.medications_administered
    : note?.medications || [];
  const given = [];
  const notGiven = [];
  meds.forEach((med) => {
    if (med?.not_given) notGiven.push(med);
    else given.push(med);
  });
  return { given, notGiven };
}

export function formatHandoverNotGivenLine(med, t) {
  const name = med.drug_name || med.name || 'Medication';
  const time = med.scheduled_time || med.time_given || '—';
  const reason = resolveDeviationReasonText(t, med) || 'reason not documented';
  return `${name} ${time} not given — ${reason}`;
}

export function formatTaskNotGivenSubtitle(t, task) {
  const reason = task?.deviationReason || task?.deviation_reason || '';
  if (!reason) return t('notGiven');
  return t('notGivenWithReason', { reason });
}
